import _ from "lodash";
import React, { useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LoginIcon from "@mui/icons-material/Login";
import { useNavigate } from "react-router-dom";
import { useModal } from "utils/SignupContext";
import { useSelector } from "react-redux";

const SectionLabel = ({ label }) => (
  <Typography
    sx={{
      px: 2,
      pt: 1.5,
      pb: 0.5,
      fontSize: "0.6rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      color: "#9ca3af",
      textTransform: "uppercase",
    }}
  >
    {label}
  </Typography>
);

const NavRow = ({ icon, title, onClick, color }) => (
  <ListItem disablePadding>
    <ListItemButton
      onClick={onClick}
      sx={{
        px: 2,
        py: 0.9,
        mx: 1,
        borderRadius: "8px",
        "&:hover": { backgroundColor: "#f3f4f6" },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: "#374151 !important", "& svg": { fontSize: "1.2rem", color: "#374151 !important" }, ...(color && { color: `${color} !important`, "& svg": { fontSize: "1.2rem", color: `${color} !important` } }) }}>
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={title}
        primaryTypographyProps={{ fontSize: "0.88rem", fontWeight: color ? 600 : 500, color: color || "#111827" }}
      />
    </ListItemButton>
  </ListItem>
);

function SecondaryAppBar1(props) {
  const {
    colors,
    anchor,
    navItems = [],
    iconItems = [],
    rightNavItems = [],
    settings = [],
    user,
  } = props;

  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const isScreenWidthLessThan1200 = useMediaQuery("(max-width:1200px)");
  const { openModal } = useModal();

  const webDetails = useSelector((state) => state.webDetails);
  const { websiteInfo } = webDetails;

  const toggleDrawer = (open) => () => setMobileOpen(open);

  const go = (path) => { setMobileOpen(false); navigate(path); };

  const getUserInitial = () => {
    if (user?.first_name) return user.first_name[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return "U";
  };

  const shopItem       = navItems.find((i) => i.title === "Shop");
  const categoriesItem = navItems.find((i) => i.title === "Categories");
  const aboutItem      = navItems.find((i) => i.title === "About");
  const andreanItem    = navItems.find((i) => i.external);
  const cartItem       = iconItems.find((i) => i.title === "Cart");
  const myOrderItem    = settings.find((i) => i.title === "My Order");
  const myAccountItem  = settings.find((i) => i.title === "My Account");
  const logoutItem     = settings.find((i) => i.title === "Logout");

  const showWhatsapp =
    websiteInfo?.whatsapp &&
    websiteInfo?.whatsapp_country_code &&
    websiteInfo?.whatsapp_status;

  const handleWhatsapp = () => {
    const url = `https://wa.me/${websiteInfo.whatsapp_country_code}${websiteInfo.whatsapp}?text=${encodeURIComponent("Hello! I would like to chat.")}`;
    window.open(url, "_blank");
  };

  const drawerContent = (
    <Box sx={{ width: 270, display: "flex", flexDirection: "column", height: "100%" }}>

      {/* ── Branded Header ── */}
      <Box sx={{ backgroundColor: colors.header_bg, px: 2, pt: 2, pb: 2 }}>
        {/* Top row: close */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mb: 1.5 }}>
          <IconButton onClick={toggleDrawer(false)} size="small" sx={{ color: colors.header_text, p: 0 }}>
            <CloseIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Box>

        {/* User info row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              backgroundColor: "rgba(255,255,255,0.2)",
              color: colors.header_text,
              fontWeight: 700,
              fontSize: "1rem",
              border: "2px solid rgba(255,255,255,0.35)",
            }}
          >
            {!_.isEmpty(user) ? getUserInitial() : null}
          </Avatar>
          <Box>
            <Typography sx={{ color: colors.header_text, fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.2 }}>
              {!_.isEmpty(user) ? `Hello, ${user?.first_name || "There"}` : "Hello, Guest"}
            </Typography>
            {!_.isEmpty(user) && (
              <Typography sx={{ color: colors.header_text, fontSize: "0.7rem", opacity: 0.8 }}>
                {user?.email}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── White body ── */}
      <Box sx={{ flex: 1, overflowY: "auto", backgroundColor: "#fff", pt: 0.5 }}>

        {/* MAIN */}
        <SectionLabel label="Main" />
        <List dense disablePadding>
          {shopItem && (
            <NavRow icon={shopItem.icon} title="Shop" onClick={() => go(shopItem.path)} />
          )}
          {categoriesItem && (
            <NavRow icon={categoriesItem.icon} title="Categories" onClick={() => go(categoriesItem.path)} />
          )}
          {andreanItem && (
            <NavRow
              icon={andreanItem.icon}
              title={andreanItem.title}
              color={andreanItem.color}
              onClick={() => { setMobileOpen(false); window.open(andreanItem.path, "_blank", "noopener,noreferrer"); }}
            />
          )}
          {cartItem && (
            <NavRow icon={cartItem.icon} title="Cart" onClick={() => go(cartItem.path || "/cart")} />
          )}
          {!_.isEmpty(user) && myOrderItem && (
            <NavRow icon={myOrderItem.icon} title="My Orders" onClick={() => go(myOrderItem.path)} />
          )}
        </List>

        <Divider sx={{ mx: 2, my: 0.5 }} />

        {/* INFO */}
        <SectionLabel label="Info" />
        <List dense disablePadding>
          {aboutItem && (
            <NavRow icon={aboutItem.icon} title="About" onClick={() => go(aboutItem.path)} />
          )}
          {rightNavItems.map(({ title, icon, path }) => (
            <NavRow key={title} icon={icon} title={title} onClick={() => go(path)} />
          ))}
        </List>

        <Divider sx={{ mx: 2, my: 0.5 }} />

        {/* ACCOUNT */}
        <SectionLabel label="Account" />
        <List dense disablePadding>
          {!_.isEmpty(user) ? (
            <>
              {myAccountItem && (
                <NavRow icon={myAccountItem.icon} title="My Account" onClick={() => go(myAccountItem.path)} />
              )}
              {logoutItem && (
                <NavRow icon={logoutItem.icon} title="Logout" onClick={() => go(logoutItem.path)} />
              )}
            </>
          ) : (
            <NavRow
              icon={<LoginIcon />}
              title="Login"
              onClick={() => { setMobileOpen(false); openModal(); }}
            />
          )}
        </List>
      </Box>

      {/* ── WhatsApp footer ── */}
      {showWhatsapp && (
        <Box
          onClick={handleWhatsapp}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            backgroundColor: "#f0faf4",
            cursor: "pointer",
            borderTop: "1px solid #d1fae5",
            "&:hover": { backgroundColor: "#e2f5ea" },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "#25D366",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WhatsAppIcon sx={{ color: "#fff", fontSize: "1.1rem" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#111827" }}>
              Need help?
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#6b7280" }}>
              Chat with us on WhatsApp
            </Typography>
          </Box>
          <ChevronRightIcon sx={{ color: "#9ca3af", fontSize: "1rem" }} />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {isScreenWidthLessThan1200 && (
        <>
          <IconButton
            size="sm"
            aria-label="open drawer"
            onClick={toggleDrawer(true)}
          >
            <MenuIcon style={{ color: colors.header_text }} />
          </IconButton>
          <Drawer
            anchor={anchor}
            open={mobileOpen}
            onClose={toggleDrawer(false)}
            sx={{
              "& .MuiDrawer-paper": {
                backgroundColor: "#fff",
                boxShadow: "4px 0 24px rgba(0,0,0,0.12)",
                borderTopRightRadius: "20px",
                borderBottomRightRadius: "20px",
                overflow: "hidden",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </>
      )}
    </>
  );
}

export default SecondaryAppBar1;
